import './users.css';
import { Button, Checkbox, Form, Input, Popconfirm, Segmented, Select, Tag } from 'antd';
import { ApartmentOutlined, DeleteOutlined, MailOutlined, PlusOutlined } from '@ant-design/icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';
import { serverFetch } from '@server';
import { showError } from '@extensions/utils';
import { useDicts } from '@extensions/dicts';
import { PageTitle } from '@extensions/layout';
import FilterBar from '@controls/filter-bar/filter-bar';
import CardList, { CardRow } from '@controls/card-list/card-list';
import ResizableDrawer from '@controls/resizable-drawer/resizable-drawer';
import UserAvatar from '../tasks/user-avatar';

const ROLE_OPTIONS = [
    { value: 0, label: 'Администратор', color: 'red' },
    { value: 10, label: 'Менеджер', color: 'blue' },
    { value: 20, label: 'Пользователь', color: 'default' },
];

const roleOf = (role) => ROLE_OPTIONS.find((r) => r.value === role);

const NEW_USER = { name: '', email: '', position: '', role: 20, departmentId: null, active: true, login: true, comment: '', messengers: '' };

const UserDrawer = ({ user, onClose, onSaved }) => {
    const { dicts } = useDicts();
    const [form] = Form.useForm();
    const [dirty, setDirty] = useState(false);
    const isNew = !user.id;
    const name = Form.useWatch('name', form);

    useEffect(() => { form.setFieldsValue(user); }, [user, form]);

    const onSave = () => {
        form.validateFields().then((values) => {
            serverFetch(isNew ? 'user' : `user/${user.id}`, {
                method: isNew ? 'POST' : 'PUT',
                bodyData: { ...user, ...values, active: values.active ?? false, login: values.login ?? false },
            })
                .then(() => { onSaved?.(); onClose?.(); })
                .catch(showError);
        });
    };

    const onDelete = () => {
        serverFetch(`user/${user.id}`, { method: 'DELETE' })
            .then(() => { onSaved?.(); onClose?.(); })
            .catch(showError);
    };

    return (
        <ResizableDrawer
            open
            storageKey="userDrawerWidth"
            defaultWidth={520}
            title={isNew ? 'Новый сотрудник' : 'Сотрудник'}
            onClose={() => onClose?.()}
            destroyOnClose
            className="resizable-drawer task-drawer"
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={onSave}
                onValuesChange={() => setDirty(true)}
                onKeyDown={(e) => {
                    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') { e.preventDefault(); form.submit(); }
                }}
            >
                <div className="task-scroll">
                    <div className="task-section user-form-head">
                        <UserAvatar name={name || '?'} size={44} />
                        <Form.Item name="name" rules={[{ required: true, message: 'Укажите имя' }]} style={{ flex: 1, margin: 0 }}>
                            <Input variant="borderless" size="large" className="user-form-name" placeholder="Имя сотрудника" autoFocus={isNew} />
                        </Form.Item>
                    </div>
                    <div className="task-section user-form">
                        <Form.Item name="email" label="Email" rules={[{ required: true, message: 'Укажите email' }, { type: 'email', message: 'Некорректный email' }]}>
                            <Input variant="filled" prefix={<MailOutlined />} placeholder="name@company.ru" />
                        </Form.Item>
                        <Form.Item name="position" label="Должность">
                            <Input variant="filled" placeholder="Например: менеджер по продажам" />
                        </Form.Item>
                        <Form.Item name="role" label="Роль">
                            <Segmented block options={ROLE_OPTIONS.map(({ value, label }) => ({ value, label }))} />
                        </Form.Item>
                        <div className="user-form-row">
                            <Form.Item name="departmentId" label="Отдел">
                                <Select variant="filled" allowClear placeholder="—" options={dicts.departments.map((d) => ({ value: d.id, label: d.name }))} />
                            </Form.Item>
                        </div>
                        <div className="user-form-row">
                            <Form.Item name="active" valuePropName="checked"><Checkbox>Активен</Checkbox></Form.Item>
                            <Form.Item name="login" valuePropName="checked"><Checkbox>Разрешён вход</Checkbox></Form.Item>
                        </div>
                        <Form.Item name="comment" label="Комментарий">
                            <Input.TextArea variant="filled" autoSize={{ minRows: 2, maxRows: 8 }} />
                        </Form.Item>
                        <Form.Item name="messengers" label="Мессенджеры">
                            <Input.TextArea
                                variant="filled"
                                autoSize={{ minRows: 1, maxRows: 4 }}
                                placeholder="Телефон, @username или id Max/Telegram — через запятую"
                            />
                        </Form.Item>
                    </div>
                    <div className="task-actions">
                        <Button type="primary" htmlType="submit" size="large" className="task-ok" disabled={!dirty}>ОК</Button>
                        <Button size="large" onClick={() => onClose?.()}>Закрыть</Button>
                        {!isNew && (
                            <Popconfirm title="Удалить сотрудника?" onConfirm={onDelete} okText="Да" cancelText="Нет">
                                <Button size="large" danger type="text" icon={<DeleteOutlined />} style={{ marginLeft: 'auto' }} />
                            </Popconfirm>
                        )}
                    </div>
                </div>
            </Form>
        </ResizableDrawer>
    );
};

const Users = () => {
    const user = useSelector((s) => s.user);
    const { dicts, reloadDicts } = useDicts();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editing, setEditing] = useState(null);
    const [filters, setFilters] = useState({ filter: '', role: null, departmentId: 0, active: null });

    const load = useCallback(() => {
        setLoading(true);
        serverFetch('user')
            .then(setItems)
            .catch(showError)
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { load(); }, [load]);

    const onChanged = () => { load(); reloadDicts(); };

    const onDelete = (r) => {
        serverFetch(`user/${r.id}`, { method: 'DELETE' }).then(onChanged).catch(showError);
    };

    const shown = useMemo(() => {
        const q = filters.filter.trim().toLowerCase();
        return items
            .filter((u) => !q || [u.name, u.email, u.position].some((s) => (s ?? '').toLowerCase().includes(q)))
            .filter((u) => filters.role == null || u.role === filters.role)
            .filter((u) => !filters.departmentId || u.departmentId === filters.departmentId)
            .filter((u) => filters.active == null || u.active === filters.active)
            .sort((a, b) => (b.active - a.active) || (a.name ?? '').localeCompare(b.name ?? ''));
    }, [items, filters]);

    const filterDefs = useMemo(() => [
        { key: 'role', label: 'Роль', emptyValue: null, options: ROLE_OPTIONS.map(({ value, label }) => ({ value, label })) },
        { key: 'departmentId', label: 'Отдел', emptyValue: 0, options: dicts.departments.map((d) => ({ value: d.id, label: d.name })) },
        { key: 'active', label: 'Активность', emptyValue: null, options: [{ value: true, label: 'активные' }, { value: false, label: 'неактивные' }] },
    ], [dicts.departments]);

    if (!user || user.role !== 0) return <Navigate to="/tasks" replace />;

    return (
        <div>
            <div className="page-toolbar">
                <PageTitle>Сотрудники</PageTitle>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => setEditing(NEW_USER)}>Добавить</Button>
                <FilterBar
                    filters={filterDefs}
                    value={filters}
                    onChange={(v) => setFilters((f) => ({ ...f, ...v }))}
                    searchValue={filters.filter}
                    onSearch={(v) => setFilters((f) => ({ ...f, filter: v }))}
                    placeholder="Поиск сотрудников"
                />
                <span className="page-toolbar-info">Всего: {shown.length}</span>
            </div>
            <CardList
                items={shown}
                loading={loading}
                emptyText="Сотрудников нет"
                deleteTitle="Удалить сотрудника?"
                onClick={(r) => { if (!r.isSuperadmin) setEditing(r); }}
                onDelete={onDelete}
                canDelete={(r) => !r.isSuperadmin}
                rowStyle={(r) => (r.active ? undefined : { opacity: 0.55 })}
                renderTitle={(r) => {
                    const role = roleOf(r.role);
                    return (
                        <>
                            <UserAvatar name={r.name} size={22} />
                            <span className="user-card-name">{r.name}</span>
                            {r.position && <span className="card-list-muted">{r.position}</span>}
                            {r.isSuperadmin
                                ? <Tag color="purple" className="user-card-tag">Суперадмин</Tag>
                                : role && <Tag color={role.color} className="user-card-tag">{role.label}</Tag>}
                            {!r.active && <Tag className="user-card-tag">неактивен</Tag>}
                            {r.active && !r.login && <Tag color="orange" className="user-card-tag">без входа</Tag>}
                        </>
                    );
                }}
                renderBody={(r) => (
                    <>
                        <CardRow icon={<MailOutlined />}>{r.email}</CardRow>
                        <CardRow icon={<ApartmentOutlined />} title="Отдел">{r.departmentName}</CardRow>
                        <CardRow className="card-list-muted card-list-wide">{r.comment}</CardRow>
                    </>
                )}
            />
            {editing && <UserDrawer user={editing} onClose={() => setEditing(null)} onSaved={onChanged} />}
        </div>
    );
};

export default Users;
